import get from 'lodash/get';
import { matchPath } from 'connected-react-router';

const columnName = (attributes) => {
  let name = '';
  if (attributes.type_name === "Seniority Bonus") name = 'sb';
  if (attributes.type_name === "Special Bonus") name = 'spb';
  if (attributes.type_name === "Performance Bonus") name = 'pb';
  if (get(attributes, 'options.consequent_contracts')) name += `_${attributes.options.consequent_contracts}`;

  return name;
}

export const convertToTableData = (resp) => {
  const table = {};

  const { data: {data} } = resp;

  data.forEach(({id, attributes}) => {
    if(!table[attributes.role_id]) {
      table[attributes.role_id] = {
        role_name: attributes.role_name,
        role_id: attributes.role_id,
        crewing_priority: attributes.crewing_priority,
      };
    }

    const column = columnName(attributes);
    table[attributes.role_id][column] = attributes.total;
    table[attributes.role_id][`${column}_id`] = id;
  });

  const tableData = Object.keys(table).map(key => ({
    role_id: key,
    ...table[key]
  })).sort((a, b) => a.crewing_priority - b.crewing_priority);

  return {
    ...resp,
    data: {
      ...resp.data,
      tableData,
    },
  }
}

// CREW-1101 — the wage matrix's columns, from the owner's component set.
//
// The order the table has always had, and it is not the catalogue's order
// alone: `Wages` is a subtotal and belongs after everything that goes into it,
// `TOTAL` last. So the set is split by `kind` rather than simply listed —
// `in_wages` and `reference` lines first, then the Wages subtotal, then the
// `in_total` lines (Owner's bonus, and now Long Service Bonus / Food
// Allowance), then TOTAL.
//
// `read` is how a cell gets its figure: a component reads `component_amounts`
// by code, a computed column reads the summed attribute the backend wrote.
const computedColumn = (key, label) => ({
  key,
  label,
  title: label,
  computed: true,
  read: (row) => row[key],
});

const componentColumn = (component) => ({
  key: component.code,
  label: component.short_title || component.title,
  title: component.title,
  computed: false,
  read: (row) => get(row, ['component_amounts', component.code], 0),
});

export const buildColumns = (components) => {
  const set = components || [];
  const inWages = set.filter(c => c.kind === 'in_wages' || c.kind === 'reference');
  const inTotal = set.filter(c => c.kind === 'in_total');
  // A `computed` component (`total`) falls through both filters ON PURPOSE.
  // It IS in the owner's set — that is how the printer learns which cell to
  // write it into — but the table already has a TOTAL column of its own below,
  // and listing it here as well would draw the figure twice.

  return [
    ...inWages.map(componentColumn),
    computedColumn('wages', 'Wages'),
    ...inTotal.map(componentColumn),
    computedColumn('total', 'TOTAL'),
  ];
};

// CREW-1299 — whose wage matrix the tab is showing, and whether it may be
// edited there.
//
//   /owners/:id/wage    the owner's own matrix                    — editable
//   /vessels/:id/wage   the vessel's own matrix, if it has one    — editable
//                       the owner's matrix, if it does not        — read-only:
//                       it belongs to the owner's page, and a figure typed
//                       here would change every other vessel he has
//
// `vesselId` is set only while the vessel's OWN cards are on screen; it is
// what the backend filters `crew_wage/scales` and `crew_wage/bonuses` by.
// Null when there is nothing to show yet: the vessel's status has not been
// read, it belongs to another vessel (a stale one from the previous page),
// or the vessel has no owner and so no matrix at all.
export const resolveMatrixContext = (pathname, vesselMatrix) => {
  const ownerPage = matchPath(pathname, { path: '/owners/:id/wage' });
  if (ownerPage) {
    return { page: 'owner', ownerId: ownerPage.params.id, vesselId: null, editable: true };
  }

  const vesselPage = matchPath(pathname, { path: '/vessels/:id/wage' });
  if (!vesselPage || !vesselMatrix?.owner_id) return null;
  if (String(vesselMatrix.vessel_id) !== String(vesselPage.params.id)) return null;

  return {
    page: 'vessel',
    ownerId: vesselMatrix.owner_id,
    vesselId: vesselMatrix.overridden ? vesselPage.params.id : null,
    editable: !!vesselMatrix.overridden,
  };
};

// The JSON:API filter for one matrix. Without `vessel_id` the backend answers
// with the owner's own rows (its filter defaults to `owner`), which is what
// both the owner page and a vessel without a matrix of its own want.
export const matrixFilter = (context, extra = {}) => ({
  owner_id: context.ownerId,
  ...(context.vesselId ? { vessel_id: context.vesselId } : {}),
  ...extra,
});

export const scaleFilter = (context, wageType, wageNationality) => matrixFilter(context, {
  scale_type: wageType,
  ...(wageType === 'alternative' ? { nationality_id: wageNationality } : {}),
});
